import { glideLocalStorage } from "../glideLocalStorage";
import { getWallets } from "@wallet-standard/app";
import { VersionedTransaction } from "@solana/web3.js";
import bs58 from "bs58";

export async function signAndSendTransaction(
  tx: VersionedTransaction,
): Promise<string> {
  const walletsApi = getWallets();
  const walletName = await glideLocalStorage.getItem("glide.solana.connector");

  // Get fresh wallet list from registry
  const allWallets = walletsApi.get();
  const wallet = allWallets.find((w) => w.name === walletName);
  if (!wallet) {
    throw new Error(`Solana wallet "${walletName}" not found`);
  }

  const signAndSendFeature = wallet.features[
    "solana:signAndSendTransaction"
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ] as any;
  if (!signAndSendFeature) {
    throw new Error(
      `Wallet "${wallet.name}" does not support solana:signAndSendTransaction feature`,
    );
  }

  // If no accounts are available, try to use the connect feature to populate accounts
  if (!wallet.accounts.length) {
    const connectFeature = wallet.features["standard:connect"] as {
      connect: () => Promise<{ accounts: { address: string }[] }>;
    };

    if (connectFeature) {
      try {
        // Try to get accounts through connect (shouldn't show UI if already connected)
        const result = await connectFeature.connect();
        if (!result.accounts.length) {
          throw new Error(
            `No accounts available in wallet "${wallet.name}". Please reconnect your wallet and try again.`,
          );
        }
      } catch {
        throw new Error(
          `No accounts available in wallet "${wallet.name}". Please reconnect your wallet and try again.`,
        );
      }
    } else {
      throw new Error(
        `No accounts available in wallet "${wallet.name}". Please reconnect your wallet and try again.`,
      );
    }
  }

  const account = wallet.accounts[0];

  const serializedTransaction = tx.serialize();

  // Determine the correct chain - check if wallet supports the chain we need
  let chain = "solana:mainnet";
  if (
    wallet.chains.includes("solana:devnet") &&
    !wallet.chains.includes("solana:mainnet")
  ) {
    chain = "solana:devnet";
  } else if (wallet.chains.includes("solana:103")) {
    chain = "solana:devnet";
  } else if (wallet.chains.includes("solana:101")) {
    chain = "solana:mainnet";
  }

  const results = await signAndSendFeature.signAndSendTransaction({
    account,
    transaction: serializedTransaction,
    chain,
  });

  if (!results.length) {
    throw new Error("No results returned from signAndSendTransaction");
  }

  // Convert Uint8Array signature to base58 string
  const signatureBytes = results[0].signature;
  const signature = bs58.encode(signatureBytes);

  return signature;
}
